import { getStartingInfo } from 'actions/whitelabel';
import { createBrowserHistory } from 'history';
import 'styles/themes/base.styl';
import { getSubOrFullDomain } from 'utils';

import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  unstable_HistoryRouter as HistoryRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

import './AppRoot.styl';
import CacheReloader from './CacheReloader';
import I18nLoader from './I18nLoader';
import Notifications from './Notifications';
import PosaleRouter from './PosaleRouter';
import {
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react';
import { getCDNBaseURL } from 'envUtils';

export const history = createBrowserHistory();

export const AppRoot = () => {
  const dispatch = useDispatch();

  const { REACT_APP_SUBDOMAIN } = process.env;
  const subdomain = REACT_APP_SUBDOMAIN || getSubOrFullDomain();

  useEffect(() => {
    const getWhitelabelData = async () => {
      await dispatch(getStartingInfo(subdomain));
    };

    getWhitelabelData();

    init({
      dsn: 'https://ecb8b6bdfd2c4bd3ae37ee620a6a782b@o348393.ingest.us.sentry.io/2212535',
      integrations: [
        reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        replayIntegration({
          maskAllText: process.env.REACT_APP_SENTRY_ENV !== 'development',
          blockAllMedia: true,
          maxReplayDuration: 180000,
        }),
      ],
      autoSessionTracking: true,
      release: process.env.REACT_APP_SENTRY_RELEASE,
      debug: process.env.REACT_APP_SENTRY_DEBUG === 'true',
      environment: process.env.REACT_APP_SENTRY_ENV || 'development',
      sampleRate:
        process.env.REACT_APP_SENTRY_ENV === 'development' ? 1.0 : 0.5, // error events
      tracesSampleRate:
        process.env.REACT_APP_SENTRY_ENV === 'development' ? 1.0 : 0.2, // performance traces
      replaysSessionSampleRate: 0, // all sessions
      replaysOnErrorSampleRate:
        process.env.REACT_APP_SENTRY_ENV === 'development' ? 1.0 : 0.2, // sessions with errors
      normalizeDepth:
        process.env.REACT_APP_SENTRY_ENV === 'development' ? 10 : 3, // state levels
      normalizeMaxBreadth: 20, // state number of properties
    });

    const themeObserver = new MutationObserver((mutationList, observer) => {
      mutationList.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'theme'
        ) {
          const theme = document.documentElement.getAttribute('theme');
          const link16 = document.getElementById('favicon-16');
          const link32 = document.getElementById('favicon-32');
          const linkApple = document.getElementById('favicon-apple-touch');
          const linkMask = document.getElementById('favicon-mask');
          const linkManifest = document.getElementById('link-manifest');

          linkApple.href = `${getCDNBaseURL()}favicons/${theme}/apple-touch-icon.png`;
          link32.href = `${getCDNBaseURL()}favicons/${theme}/favicon-32x32.png`;
          link16.href = `${getCDNBaseURL()}favicons/${theme}/favicon-16x16.png`;
          linkMask.href = `${getCDNBaseURL()}favicons/${theme}/safari.svg`;
          linkManifest.href = `${getCDNBaseURL()}favicons/${theme}/manifest.webmanifest`;

          return import(`styles/themes/${theme}.styl`);
        }

        return null;
      });
    });

    themeObserver.observe(document.documentElement, {
      attributes: true,
      childList: false,
      subtree: false,
    });

    return () => {
      themeObserver.disconnect();
    };
  }, [subdomain, dispatch]);

  return (
    <CacheReloader>
      <I18nLoader>
        <HistoryRouter history={history}>
          <Notifications />
          <PosaleRouter />
        </HistoryRouter>
      </I18nLoader>
    </CacheReloader>
  );
};

export default AppRoot;
